// The front page (02-DESIGN/03-console/03-the-signed-out-site.md; decisions
// 0054 and 0071): `/` for a person who is not signed in, on an install that
// lets a stranger sign up. It sells the agentic cloud: the opening with
// *Get started* and the drawn cloud, the statement, the four positions, what
// is already there, and the close — *Decide once.* — with the console's
// sign-in card. Light only. One page, no path of its own; its place-markers
// are the fragments `#sign-in`, which names the card, and `#why`, the
// statement. Nothing here is fetched from elsewhere, sets a cookie or writes
// to the browser's storage until the person starts a sign-in.

import { useEffect, useRef, type MouseEvent, type ReactNode } from "react";

import { Wordmark } from "../../shell/Mark.tsx";
import { Button, Notice } from "../../ui/components.tsx";
import { usePage } from "../../platform/session.tsx";
import { useFormState } from "../../ui/form.tsx";
import { usePlatformSignIn } from "../entry/platformSignIn.ts";
import { drawCloud } from "./cloud.ts";
import { requestAccessOf } from "./front.ts";
import { RequestAccessForm } from "./RequestAccess.tsx";
import { buildConstellation, SEEDS, SIDE } from "./constellation.ts";
import { reducedMotion } from "./draw.ts";
import { drawSubstrate } from "./substrate.ts";
import {
  ALREADY,
  ALREADY_HEADING,
  CLOSE,
  FOOTNOTE,
  GET_STARTED,
  REQUEST_ACCESS,
  HEADLINE,
  INTERFACES,
  LEDE,
  POSITIONS,
  SIGN_IN,
  SIGN_IN_TO_WORKSPACE,
  STATEMENT,
  UNDER_ACTIONS,
  VISUAL_DESCRIPTION,
  VISUAL_NAME,
  WHY,
} from "./words.ts";
import "./site.css";

/** bringTo scrolls an element into view, at once for a person who prefers reduced motion. */
function bringTo(el: HTMLElement | null, block: ScrollLogicalPosition) {
  el?.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block });
}

/** focusCard moves the focus to the sign-in card's first button and brings the card into view. */
function focusCard() {
  const first = document.querySelector<HTMLElement>("#sign-in button");
  if (first === null) {
    return;
  }
  first.focus({ preventScroll: true });
  bringTo(document.getElementById("sign-in"), "center");
}

/** focusWhy moves the focus to the statement and brings it into view. */
function focusWhy() {
  const why = document.getElementById("why");
  why?.focus({ preventScroll: true });
  bringTo(why, "start");
}

function SignInLink({ children = SIGN_IN }: { children?: ReactNode }) {
  return (
    <a
      href="#sign-in"
      className="site-link"
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        focusCard();
      }}
    >
      {children}
    </a>
  );
}

/** The visual: the cloud on a canvas, one image to a screen reader, with a name and a description. */
function Cloud() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => (ref.current === null ? undefined : drawCloud(ref.current)), []);
  return (
    <div className="site-art">
      <div
        role="img"
        aria-label={VISUAL_NAME}
        aria-describedby="site-art-description"
        className="site-art-frame"
      >
        <canvas ref={ref} />
      </div>
      <p id="site-art-description" className="sr-only">
        {VISUAL_DESCRIPTION}
      </p>
    </div>
  );
}

/** The substrate under the closing words: decoration, hidden from a screen reader. */
function Substrate() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => (ref.current === null ? undefined : drawSubstrate(ref.current)), []);
  return <canvas ref={ref} className="site-substrate" aria-hidden="true" />;
}

/** A position's constellation: decoration, hidden from a screen reader. */
function Constellation({ seed }: { seed: number }) {
  const c = buildConstellation(seed);
  return (
    <svg className="site-constellation" viewBox={`0 0 ${SIDE} ${SIDE}`} aria-hidden="true" focusable="false">
      {c.lines.map((l) => (
        <line key={`${l.x1},${l.y1},${l.x2},${l.y2}`} {...l} className="site-mesh-line" />
      ))}
      {c.points.map((p, i) => (
        <circle
          key={`${p.x},${p.y}`}
          cx={p.x}
          cy={p.y}
          r={p.r}
          className={i === c.hot ? "site-mesh-hot" : "site-mesh-point"}
        />
      ))}
    </svg>
  );
}

export function SitePage({ card }: { card: ReactNode }) {
  const { platform, origin } = usePage();
  const { choice, busy, err, signIn } = usePlatformSignIn();
  // Where sign-up is off, the page asks instead of admitting (decisions 0074
  // and 0076): a mailto: address opens the request form in this column, an
  // https: address is a link.
  const ask = requestAccessOf(platform);
  const askAddress = ask !== undefined && ask.startsWith("mailto:") ? ask.slice("mailto:".length) : undefined;
  const form = useFormState<"request-access">();
  // askForm is the address the open form mails to, and nothing while the form is closed.
  const askForm = form.open === "request-access" ? askAddress : undefined;
  const asking = askForm !== undefined;

  // Arriving at `/#sign-in` lands on the card, as the link does.
  useEffect(() => {
    if (window.location.hash === "#sign-in") {
      focusCard();
    }
  }, []);

  return (
    <div className="site">
      <header className="site-top">
        <Wordmark decorative />
        <nav className="site-nav" aria-label="On this page">
          <a
            href="#why"
            className="site-why"
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              focusWhy();
            }}
          >
            {WHY}
          </a>
          <SignInLink />
        </nav>
      </header>

      <main>
        <section className="site-open" aria-labelledby="site-headline">
          <div className="site-open-text">
            <div className={asking ? "site-visually-hidden" : undefined}>
              <h1 id="site-headline">{HEADLINE}</h1>
            </div>
            {askForm !== undefined ? (
              <RequestAccessForm origin={origin} address={askForm} onClose={form.close} />
            ) : (
              <>
                <p className="site-lede">{LEDE}</p>
                <div className="site-actions">
                  {ask === undefined ? (
                    <Button kind="primary" size="lg" onClick={signIn} disabled={choice === undefined || busy}>
                      {GET_STARTED}
                    </Button>
                  ) : askAddress !== undefined ? (
                    <Button
                      kind="primary"
                      size="lg"
                      onClick={() => {
                        form.show("request-access");
                      }}
                    >
                      {REQUEST_ACCESS}
                    </Button>
                  ) : (
                    <a className="btn primary lg" href={ask}>
                      {REQUEST_ACCESS}
                    </a>
                  )}
                  <SignInLink>{SIGN_IN_TO_WORKSPACE}</SignInLink>
                </div>
                {err !== undefined && (
                  <div className="site-err">
                    <Notice kind="bad">{err}</Notice>
                  </div>
                )}
                {ask === undefined && <p className="site-under">{UNDER_ACTIONS}</p>}
                <p className="site-interfaces">
                  {INTERFACES.map((name, i) => (
                    <span key={name}>
                      {i > 0 && <span aria-hidden="true"> · </span>}
                      {name}
                    </span>
                  ))}
                </p>
              </>
            )}
          </div>
          <Cloud />
        </section>

        <section className="site-section site-statement" id="why" tabIndex={-1} aria-labelledby="site-why">
          <h2 id="site-why">{STATEMENT.heading}</h2>
          <p>{STATEMENT.text}</p>
        </section>

        {POSITIONS.map((p, i) => (
          <section
            key={p.heading}
            className={i % 2 === 1 ? "site-section site-position flip" : "site-section site-position"}
            aria-labelledby={`site-position-${i + 1}`}
          >
            <div className="site-position-text">
              <h2 id={`site-position-${i + 1}`}>{p.heading}</h2>
              <p>{p.text}</p>
            </div>
            <Constellation seed={SEEDS[i] ?? i + 1} />
          </section>
        ))}

        <section className="site-section site-already" aria-labelledby="site-already">
          <h2 id="site-already">{ALREADY_HEADING}</h2>
          <ul>
            {ALREADY.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>

        <section className="site-section site-close" aria-labelledby="site-close">
          <div className="site-close-text">
            <h2 id="site-close">{CLOSE.heading}</h2>
            <p>{CLOSE.text}</p>
            <Substrate />
          </div>
          <div className="site-close-card">{card}</div>
        </section>
      </main>

      <footer className="site-foot">
        <Wordmark decorative />
        <SignInLink />
        <p>{FOOTNOTE}</p>
      </footer>
    </div>
  );
}
